import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatMXN, imageUrl, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { CommercialAdvisor } from "./CommercialAdvisor";
import {
  Plus, PencilSimple, Trash, Envelope, House, X, UploadSimple, MapPin,
  Eye, Pause, Play, CheckCircle, FileText, MagnifyingGlass, DotsThreeVertical, Copy, ChartLineUp, Buildings, Image as ImageIcon,
} from "@phosphor-icons/react";
import { FlyerModal } from "../components/FlyerModal";
import { ShareMenu } from "../components/ShareMenu";

const STATUS_META = {
  activa:   { label: "Activa",   dot: "bg-emerald-500", chip: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  pausada:  { label: "Pausada",  dot: "bg-amber-500",   chip: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  vendida:  { label: "Vendida",  dot: "bg-sky-500",     chip: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
  borrador: { label: "Borrador", dot: "bg-zinc-500",    chip: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30" },
};

const STATUS_TABS = [
  { key: "todas",    label: "Todas" },
  { key: "activa",   label: "Activas" },
  { key: "pausada",  label: "Pausadas" },
  { key: "vendida",  label: "Vendidas" },
  { key: "borrador", label: "Borradores" },
];

export function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("todas");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const [propsRes, inqRes, sumRes] = await Promise.all([
        api.get("/properties", { params: { agent_id: user.id, limit: 200 } }),
        api.get("/inquiries").catch(() => ({ data: [] })),
        api.get("/dashboard/summary").catch(() => ({ data: null })),
      ]);
      setProperties(propsRes.data);
      setInquiries(inqRes.data);
      setSummary(sumRes.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user]);

  // Reset selection when filter changes
  useEffect(() => { setSelectedIds([]); }, [statusFilter, search]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return properties.filter(p => {
      if (statusFilter !== "todas" && (p.status || "activa") !== statusFilter) return false;
      if (!s) return true;
      return (
        p.title?.toLowerCase().includes(s) ||
        p.city?.toLowerCase().includes(s) ||
        p.address?.toLowerCase().includes(s)
      );
    });
  }, [properties, statusFilter, search]);

  const allVisibleSelected = filtered.length > 0 && filtered.every(p => selectedIds.includes(p.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) setSelectedIds(prev => prev.filter(id => !filtered.some(p => p.id === id)));
    else setSelectedIds(prev => Array.from(new Set([...prev, ...filtered.map(p => p.id)])));
  };
  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const changeStatus = async (id, newStatus) => {
    try {
      const { data } = await api.patch(`/properties/${id}/status`, { status: newStatus });
      setProperties(prev => prev.map(p => p.id === id ? { ...p, status: data.status } : p));
      toast.success(`Estado actualizado a "${STATUS_META[newStatus]?.label ?? newStatus}"`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkStatus = async (newStatus) => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Cambiar ${selectedIds.length} propiedad(es) a "${STATUS_META[newStatus].label}"?`)) return;
    try {
      const { data } = await api.post("/properties/bulk/status", { ids: selectedIds, status: newStatus });
      toast.success(`${data.modified} propiedad(es) actualizadas`);
      setSelectedIds([]);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.length} propiedad(es)? Esta acción no se puede deshacer.`)) return;
    try {
      const { data } = await api.post("/properties/bulk/delete", { ids: selectedIds });
      toast.success(`${data.deleted} propiedad(es) eliminadas`);
      setSelectedIds([]);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const delOne = async (id) => {
    if (!window.confirm("¿Eliminar esta propiedad?")) return;
    try {
      await api.delete(`/properties/${id}`);
      setProperties(prev => prev.filter(p => p.id !== id));
      toast.success("Propiedad eliminada");
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const duplicateOne = async (id) => {
    try {
      const { data } = await api.post(`/properties/${id}/duplicate`);
      toast.success("Copia creada como borrador");
      // Refresh to show new draft.
      loadAll();
      return data;
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const s = summary?.properties?.by_status || {};
  const tabCounts = {
    todas:    summary?.properties?.total ?? properties.length,
    activa:   s.activa    ?? 0,
    pausada:  s.pausada   ?? 0,
    vendida:  s.vendida   ?? 0,
    borrador: s.borrador  ?? 0,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Panel de agente</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-2 tracking-tight" data-testid="dashboard-title">
            Hola, {user?.name?.split(" ")[0]}
          </h1>
          <p className="mt-2 text-[#C8A24A]/70">Administra tus publicaciones y consultas.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/dashboard/agencia">
            <Button variant="outline" className="h-12 px-4" data-testid="dashboard-agency-btn">
              <Buildings size={18} weight="duotone" className="mr-2" /> Agencia
            </Button>
          </Link>
          <Link to="/dashboard/analytics">
            <Button variant="outline" className="h-12 px-4" data-testid="dashboard-analytics-btn">
              <ChartLineUp size={18} weight="duotone" className="mr-2" /> Analíticas
            </Button>
          </Link>
          <Button onClick={() => navigate("/dashboard/nueva")} className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12 px-6" data-testid="dashboard-new-btn">
            <Plus size={18} weight="bold" className="mr-2" /> Publicar propiedad
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
        <KpiCard icon={House}       label="Total"       value={tabCounts.todas}    testId="kpi-total" />
        <KpiCard icon={CheckCircle} label="Activas"     value={tabCounts.activa}   testId="kpi-active" tint="emerald" />
        <KpiCard icon={Pause}       label="Pausadas"    value={tabCounts.pausada}  testId="kpi-paused" tint="amber" />
        <KpiCard icon={FileText}    label="Borradores"  value={tabCounts.borrador} testId="kpi-drafts" tint="zinc" />
        <KpiCard icon={Envelope}    label="Sin leer"    value={summary?.inquiries?.unread ?? 0} testId="kpi-unread" tint="gold" />
      </div>

      {/* ATLAS Commercial Advisor widget (Fase 5.2) */}
      <div className="mb-10">
        <CommercialAdvisor compact maxItems={3} />
      </div>

      <Tabs defaultValue="properties" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="properties" data-testid="tab-properties">Mis propiedades</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">
            Consultas {summary?.inquiries?.unread > 0 && (
              <span className="ml-2 bg-[#C8A24A] text-black text-xs font-bold rounded-full h-4 min-w-[16px] px-1 inline-flex items-center justify-center">
                {summary.inquiries.unread}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="properties">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex flex-wrap gap-1 bg-[#111111] border border-[#C8A24A]/20 rounded-xl p-1" data-testid="status-tabs">
              {STATUS_TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setStatusFilter(t.key)}
                  data-testid={`status-tab-${t.key}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    statusFilter === t.key
                      ? "bg-[#C8A24A] text-[#111111]"
                      : "text-[#C8A24A]/70 hover:text-[#C8A24A]"
                  }`}
                >
                  {t.label}
                  <span className="ml-1.5 opacity-70">({tabCounts[t.key] ?? 0})</span>
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C8A24A]/60" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por título, ciudad o dirección"
                className="pl-9"
                data-testid="dashboard-search-input"
              />
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-xl bg-[#C8A24A]/10 border border-[#C8A24A]/40" data-testid="bulk-action-bar">
              <span className="text-sm text-[#C8A24A] font-semibold mr-2">
                {selectedIds.length} seleccionada(s)
              </span>
              <Button size="sm" variant="outline" onClick={() => bulkStatus("activa")} data-testid="bulk-activate-btn">
                <Play size={14} className="mr-1" /> Activar
              </Button>
              <Button size="sm" variant="outline" onClick={() => bulkStatus("pausada")} data-testid="bulk-pause-btn">
                <Pause size={14} className="mr-1" /> Pausar
              </Button>
              <Button size="sm" variant="outline" onClick={() => bulkStatus("vendida")} data-testid="bulk-sold-btn">
                <CheckCircle size={14} className="mr-1" /> Marcar vendida
              </Button>
              <Button size="sm" variant="outline" onClick={bulkDelete} className="text-destructive hover:text-destructive" data-testid="bulk-delete-btn">
                <Trash size={14} className="mr-1" /> Eliminar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="ml-auto" data-testid="bulk-clear-btn">
                <X size={14} className="mr-1" /> Limpiar
              </Button>
            </div>
          )}

          {loading ? (
            <div className="grid gap-4">
              {[0,1,2].map(i => (
                <div key={i} className="h-32 bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/10 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyPropertiesState hasProps={properties.length > 0} statusFilter={statusFilter} />
          ) : (
            <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 overflow-hidden" data-testid="properties-list">
              <div className="px-4 py-3 border-b border-[#C8A24A]/10 flex items-center gap-3">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                  aria-label="Seleccionar todas"
                />
                <span className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">
                  {filtered.length} propiedad(es)
                </span>
              </div>
              <div className="divide-y divide-[#C8A24A]/10">
                {filtered.map(p => (
                  <PropertyRow
                    key={p.id}
                    property={p}
                    selected={selectedIds.includes(p.id)}
                    onToggle={() => toggleOne(p.id)}
                    onChangeStatus={changeStatus}
                    onDelete={delOne}
                    onDuplicate={duplicateOne}
                  />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="inquiries">
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6" data-testid="dashboard-inquiries-panel">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Centro comercial</p>
                <h3 className="font-heading text-2xl mt-1">Bandeja de leads</h3>
                <p className="text-sm text-[#C8A24A]/70 mt-1">Gestiona consultas, respuestas, estados y seguimiento.</p>
              </div>
              <Link to="/dashboard/leads">
                <Button className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-5" data-testid="open-lead-center-btn">
                  Abrir Lead Center →
                </Button>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              <div className="rounded-xl bg-[#0A0A0A] border border-[#C8A24A]/10 p-4">
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Total de consultas</p>
                <p className="text-3xl font-heading font-bold mt-1">{summary?.inquiries?.total ?? inquiries.length}</p>
              </div>
              <div className="rounded-xl bg-[#0A0A0A] border border-[#C8A24A]/10 p-4">
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Sin leer</p>
                <p className="text-3xl font-heading font-bold mt-1 text-[#C8A24A]">{summary?.inquiries?.unread ?? 0}</p>
              </div>
            </div>
            {inquiries.slice(0, 3).length > 0 && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Recientes</p>
                <div className="space-y-2">
                  {inquiries.slice(0, 3).map(inq => (
                    <Link
                      key={inq.id}
                      to="/dashboard/leads"
                      className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A] border border-[#C8A24A]/10 hover:border-[#C8A24A]/40 transition-colors"
                      data-testid={`inquiry-preview-${inq.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm truncate ${inq.read ? "text-[#C8A24A]/80" : "text-[#C8A24A] font-semibold"}`}>
                          {inq.name} · {inq.property_title}
                        </p>
                        <p className="text-xs text-[#C8A24A]/60 truncate">{inq.message}</p>
                      </div>
                      {!inq.read && <span className="ml-3 w-2 h-2 rounded-full bg-[#C8A24A] flex-shrink-0" />}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmptyPropertiesState({ hasProps, statusFilter }) {
  if (hasProps) {
    return (
      <div className="bg-[#1A1A1A] rounded-2xl border border-dashed border-[#C8A24A]/20 p-16 text-center" data-testid="empty-filtered-state">
        <MagnifyingGlass size={40} className="mx-auto text-[#C8A24A]/30 mb-4" />
        <p className="font-heading text-2xl mb-2">Sin resultados</p>
        <p className="text-sm text-[#C8A24A]/60">
          No hay propiedades que coincidan con el filtro "{statusFilter}" o tu búsqueda.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-[#1A1A1A] rounded-2xl border border-dashed border-[#C8A24A]/20 p-16 text-center" data-testid="empty-state">
      <House size={40} className="mx-auto text-[#C8A24A]/30 mb-4" />
      <p className="font-heading text-2xl mb-2">Aún no publicas propiedades</p>
      <p className="text-sm text-[#C8A24A]/60 mb-4">Publica tu primera propiedad y comienza a recibir consultas.</p>
      <Link to="/dashboard/nueva">
        <Button className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]" data-testid="dashboard-first-publish-btn">
          <Plus size={16} weight="bold" className="mr-1" /> Publicar mi primera propiedad
        </Button>
      </Link>
    </div>
  );
}

function PropertyRow({ property: p, selected, onToggle, onChangeStatus, onDelete, onDuplicate }) {
  const status = p.status || "activa";
  const meta = STATUS_META[status] || STATUS_META.activa;
  const [menuOpen, setMenuOpen] = useState(false);
  const [flyerOpen, setFlyerOpen] = useState(false);
  const canFlyer = status === "activa" && (p.images?.length || 0) > 0;

  return (
    <div
      className={`px-4 py-4 flex flex-col md:flex-row gap-4 items-start md:items-center transition-colors ${
        selected ? "bg-[#C8A24A]/5" : "hover:bg-[#C8A24A]/[0.02]"
      }`}
      data-testid={`dashboard-property-${p.id}`}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={onToggle}
        data-testid={`select-checkbox-${p.id}`}
        aria-label={`Seleccionar ${p.title}`}
      />
      <div className="w-full md:w-32 aspect-[4/3] md:aspect-square rounded-lg overflow-hidden bg-[#0A0A0A] flex-shrink-0">
        {p.images?.[0] ? (
          <img src={imageUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#C8A24A]/30">
            <House size={24} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${meta.chip}`} data-testid={`status-badge-${p.id}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
          <span className="text-xs uppercase tracking-wider text-[#C8A24A]/60">
            {p.operation === "renta" ? "Renta" : "Venta"} · {p.property_type}
          </span>
        </div>
        <h3 className="font-heading text-lg mt-1 truncate">{p.title}</h3>
        <p className="text-xs text-[#C8A24A]/70 flex items-center gap-1 mt-0.5">
          <MapPin size={12} weight="fill" className="text-[#C8A24A]" /> {p.city}, {p.state}
        </p>
        <p className="text-base font-heading font-bold text-[#C8A24A] mt-1">{formatMXN(p.price)}</p>
      </div>
      <div className="flex flex-wrap gap-2 md:gap-1 md:flex-col md:items-end w-full md:w-auto">
        <Link to={`/propiedades/${p.id}`}>
          <Button variant="outline" size="sm" data-testid={`view-btn-${p.id}`}>
            <Eye size={14} className="mr-1" /> Ver
          </Button>
        </Link>
        <Link to={`/dashboard/editar/${p.id}`}>
          <Button variant="outline" size="sm" data-testid={`edit-btn-${p.id}`}>
            <PencilSimple size={14} className="mr-1" /> Editar
          </Button>
        </Link>
        {canFlyer && (
          <Button
            size="sm"
            onClick={() => setFlyerOpen(true)}
            className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]"
            data-testid={`flyer-btn-${p.id}`}
          >
            <ImageIcon size={14} className="mr-1" weight="duotone" /> Flyer
          </Button>
        )}
        <ShareMenu property={p} variant="icon" className="h-9 w-9" />
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMenuOpen(v => !v)}
            data-testid={`more-btn-${p.id}`}
            aria-label="Más acciones"
          >
            <DotsThreeVertical size={14} weight="bold" />
          </Button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-1 w-48 bg-[#1A1A1A] border border-[#C8A24A]/30 rounded-lg shadow-xl z-20 py-1" data-testid={`more-menu-${p.id}`}>
                {status !== "activa" && (
                  <MenuItem icon={Play} onClick={() => { onChangeStatus(p.id, "activa"); setMenuOpen(false); }} testId={`menu-activate-${p.id}`}>Activar</MenuItem>
                )}
                {status !== "pausada" && (
                  <MenuItem icon={Pause} onClick={() => { onChangeStatus(p.id, "pausada"); setMenuOpen(false); }} testId={`menu-pause-${p.id}`}>Pausar</MenuItem>
                )}
                {status !== "vendida" && (
                  <MenuItem icon={CheckCircle} onClick={() => { onChangeStatus(p.id, "vendida"); setMenuOpen(false); }} testId={`menu-sold-${p.id}`}>Marcar vendida</MenuItem>
                )}
                {status !== "borrador" && (
                  <MenuItem icon={FileText} onClick={() => { onChangeStatus(p.id, "borrador"); setMenuOpen(false); }} testId={`menu-draft-${p.id}`}>Mover a borrador</MenuItem>
                )}
                <div className="border-t border-[#C8A24A]/10 my-1" />
                <MenuItem icon={Copy} onClick={() => { onDuplicate(p.id); setMenuOpen(false); }} testId={`menu-duplicate-${p.id}`}>Duplicar</MenuItem>
                {canFlyer && (
                  <MenuItem icon={ImageIcon} onClick={() => { setFlyerOpen(true); setMenuOpen(false); }} testId={`menu-flyer-${p.id}`}>Crear flyer</MenuItem>
                )}
                <div className="border-t border-[#C8A24A]/10 my-1" />
                <MenuItem icon={Trash} danger onClick={() => { onDelete(p.id); setMenuOpen(false); }} testId={`menu-delete-${p.id}`}>Eliminar</MenuItem>
              </div>
            </>
          )}
        </div>
      </div>
      {flyerOpen && (
        <FlyerModal open={flyerOpen} property={p} onClose={() => setFlyerOpen(false)} />
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, children, onClick, danger, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[#C8A24A]/10 ${
        danger ? "text-red-400 hover:text-red-300" : "text-[#C8A24A]/90"
      }`}
    >
      <Icon size={14} /> {children}
    </button>
  );
}

function KpiCard({ icon: Icon, label, value, testId, tint = "gold" }) {
  const tintMap = {
    gold:    { icon: "text-[#C8A24A]",  border: "border-[#C8A24A]/20" },
    emerald: { icon: "text-emerald-400", border: "border-emerald-500/20" },
    amber:   { icon: "text-amber-400",   border: "border-amber-500/20" },
    zinc:    { icon: "text-zinc-400",    border: "border-zinc-500/20" },
  };
  const t = tintMap[tint] || tintMap.gold;
  return (
    <div className={`p-4 rounded-2xl bg-[#1A1A1A] border ${t.border}`} data-testid={testId}>
      <Icon size={20} weight="duotone" className={t.icon} />
      <p className="text-xs uppercase tracking-wider mt-2 text-[#C8A24A]/60 font-semibold">{label}</p>
      <p className="text-2xl font-heading font-bold mt-0.5">{value}</p>
    </div>
  );
}

export function PropertyForm({ mode = "create" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", price: "", property_type: "casa", operation: "venta",
    bedrooms: 2, bathrooms: 1, area_m2: 0, address: "", city: "", state: "",
    latitude: "", longitude: "", images: [], features: [], status: "activa",
  });
  const [featureInput, setFeatureInput] = useState("");

  useEffect(() => {
    if (mode === "edit" && id) {
      api.get(`/properties/${id}`).then(({ data }) => {
        setForm({
          ...data,
          price: String(data.price),
          latitude: data.latitude ?? "",
          longitude: data.longitude ?? "",
        });
      });
    }
  }, [id, mode]);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const total = files.length;
    const results = new Array(total);
    let done = 0, failed = 0;
    const CONCURRENCY = 3;
    let idx = 0;
    const worker = async () => {
      while (idx < total) {
        const i = idx++;
        const file = files[i];
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60_000,
          });
          results[i] = data.url;
        } catch { failed++; results[i] = null; }
        finally {
          done++;
          if (total > 5) toast.message(`Subiendo ${done}/${total}...`, { id: "upload-progress" });
        }
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
      const urls = results.filter(Boolean);
      if (urls.length) setForm(f => ({ ...f, images: [...f.images, ...urls] }));
      if (failed === 0)     toast.success(`${urls.length} imagen(es) cargada(s)`);
      else if (urls.length) toast.success(`${urls.length} de ${total} imagen(es) cargada(s). ${failed} falló(fallaron), reintenta esas.`);
      else                  toast.error("No se pudieron subir las imágenes. Reintenta.");
    } finally { setUploading(false); }
  };

  const removeImage = (i) => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const addFeature = () => {
    if (!featureInput.trim()) return;
    setForm(f => ({ ...f, features: [...f.features, featureInput.trim()] }));
    setFeatureInput("");
  };

  const submit = async (e, statusOverride) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        status: statusOverride || form.status || "activa",
        price: Number(form.price),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        area_m2: Number(form.area_m2),
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
      };
      if (mode === "edit") {
        await api.put(`/properties/${id}`, payload);
        toast.success("Propiedad actualizada");
      } else {
        await api.post("/properties", payload);
        toast.success(statusOverride === "borrador" ? "Guardado como borrador" : "Propiedad publicada");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/dashboard" className="text-sm text-[#C8A24A]/70 hover:text-[#C8A24A]">← Volver al panel</Link>
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-4 mb-6 sm:mb-8" data-testid="form-title">
        {mode === "edit" ? "Editar propiedad" : "Publicar propiedad"}
      </h1>

      <form onSubmit={submit} className="space-y-8" data-testid="property-form">
        {/* Images */}
        <Section title="Fotografías">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {form.images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-[#1A1A1A] group">
                <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-image-${i}`}>
                  <X size={14} />
                </button>
              </div>
            ))}
            <label className="aspect-square rounded-xl border-2 border-dashed border-[#C8A24A]/20 hover:border-[#C8A24A] cursor-pointer flex flex-col items-center justify-center text-[#C8A24A]/70 hover:text-[#C8A24A] transition-colors" data-testid="upload-label">
              <UploadSimple size={24} />
              <span className="text-xs mt-1">{uploading ? "Subiendo..." : "Añadir"}</span>
              <input type="file" accept="image/*" multiple className="hidden" disabled={uploading}
                onChange={(e) => e.target.files.length && uploadFiles([...e.target.files])} data-testid="upload-input" />
            </label>
          </div>
        </Section>

        {/* Basic */}
        <Section title="Información básica">
          <div className="grid gap-4">
            <Field label="Título"><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title-input" /></Field>
            <Field label="Descripción"><Textarea rows={5} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Operación">
                <Select value={form.operation} onValueChange={(v) => setForm({ ...form, operation: v })}>
                  <SelectTrigger data-testid="form-operation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="venta">Venta</SelectItem>
                    <SelectItem value="renta">Renta</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tipo">
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casa">Casa</SelectItem>
                    <SelectItem value="departamento">Departamento</SelectItem>
                    <SelectItem value="terreno">Terreno</SelectItem>
                    <SelectItem value="oficina">Oficina</SelectItem>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="bodega">Bodega</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Precio (MXN)">
              <Input type="number" required min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
            </Field>
          </div>
        </Section>

        <Section title="Detalles">
          <div className="grid grid-cols-3 gap-4">
            <Field label="Recámaras"><Input type="number" min="0" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} data-testid="form-bedrooms" /></Field>
            <Field label="Baños"><Input type="number" step="0.5" min="0" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} data-testid="form-bathrooms" /></Field>
            <Field label="Superficie (m²)"><Input type="number" min="0" value={form.area_m2} onChange={(e) => setForm({ ...form, area_m2: e.target.value })} data-testid="form-area" /></Field>
          </div>
        </Section>

        <Section title="Ubicación">
          <div className="grid gap-4">
            <Field label="Dirección"><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="form-address" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Ciudad"><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="form-city" /></Field>
              <Field label="Estado"><Input required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="form-state" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Latitud (opcional)"><Input type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="form-lat" placeholder="19.4326" /></Field>
              <Field label="Longitud (opcional)"><Input type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="form-lng" placeholder="-99.1332" /></Field>
            </div>
            <p className="text-xs text-[#C8A24A]/60">Añade coordenadas para mostrar el mapa en la página de detalles.</p>
          </div>
        </Section>

        <Section title="Características (opcional)">
          <div className="flex gap-2 mb-3">
            <Input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
              placeholder="Ej. Alberca, estacionamiento" data-testid="form-feature-input" />
            <Button type="button" variant="outline" onClick={addFeature} data-testid="form-add-feature-btn">Añadir</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.features.map((f, i) => (
              <span key={i} className="px-3 py-1 bg-[#1A1A1A] rounded-full text-sm inline-flex items-center gap-2">
                {f}
                <button type="button" onClick={() => setForm(prev => ({ ...prev, features: prev.features.filter((_, idx) => idx !== i) }))}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </Section>

        <div className="flex gap-3 flex-wrap">
          <Button type="submit" disabled={loading} className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12 px-8" data-testid="form-submit-btn">
            {loading ? "Guardando..." : mode === "edit" ? "Actualizar" : "Publicar"}
          </Button>
          {mode === "create" && (
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              className="h-12"
              data-testid="form-save-draft-btn"
              onClick={(e) => submit(e, "borrador")}
            >
              Guardar como borrador
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => navigate("/dashboard")} className="h-12">Cancelar</Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6">
      <h2 className="font-heading text-xl mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
